// store/slices/b2cAdminSlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  addProductInCollection,
  addToCarts,
  createCoupon,
  editCollection,
  fetchAllAdminUsers,
  fetchAllAdminUsersCarts,
  fetchAllAdminUsersOrders,
  fetchAllCoupon,
  fetchAlldealerProductSale,
  fetchAlldealerSKUSale,
  fetchAllPreOrderUserDetails,
  fetchAllProductAndSKU,
  fetchAllProductInventory,
  fetchAllUserCarts,
  fetchAllUsersRole,
  fetchAllUsersShipping,
  fetchB2cDashboard,
  getAllInventory,
  getB2cOrderDetails,
  getCollectionDetail,
  getCollections,
  removeAllFromPreorderCarts,
  removeFromCarts,
  removeProductFromCollection,
  toggleCollection,
  toggleCoupon,
  toggleProductStatus,
  toggleSKUStatus,
  updateB2cOrderStatus,
  updateDeliveredStatus,
  updateUserRole,
  updateUserShipping,
} from "../../api/b2c/b2cApi";
import {
  AddCart,
  AdminAllCarts,
  AdminUserRole,
  B2COrderDetail,
  B2COrdersData,
  B2CUser,
  CartResponse,
  cCoupon,
  ColData,
  Collection,
  CollectionProductResponse,
  ColName,
  Coupon,
  InvoiceData,
  PreorderUser,
  ProductAndSKUResponse,
  ProductInventoryItem,
  ProductSale,
  ProductWithSkus,
  RemoveCart,
  SKUProductSale,
  UpdateDeliveredStatus,
  UserShipping,
} from "@/types/b2c";
import { UpdateOrderStatus } from "@/types/api";

interface B2CState {
  dashboard: B2COrdersData | null;
  users: B2CUser[];
  orders: B2COrderDetail[];
  orderB2cDetail: InvoiceData | null;
  allCarts: AdminAllCarts[];
  userCarts: CartResponse | null;
  userRole: AdminUserRole[];
  coupon: Coupon[];
  collectionList: Collection[];
  collectionDetail: CollectionProductResponse | null;
  collectionInventory: ProductWithSkus[];
  inventory: ProductInventoryItem[];
  productList: ProductAndSKUResponse | null;
  userShipping: UserShipping[];
  productSales: ProductSale[];
  skuSale: SKUProductSale[];
  preOrder: PreorderUser[];
  loading: boolean;
  orderLoading: boolean;
  collectionId: number | string | null;
  userCartLoading: boolean;
  addCartLoading: boolean;
  removeCartLoading: boolean;
  dashboardLoading: boolean;
  invoiceDataLoading:boolean;
  error: string | null;
}

const initialState: B2CState = {
  dashboard: null,
  users: [],
  allCarts: [],
  userCarts: null,
  orders: [],
  orderB2cDetail:null,
  userRole: [],
  coupon: [],
  inventory: [],
  productList: null,
  collectionList: [],
  collectionDetail: null,
  collectionInventory: [],
  userShipping: [],
  productSales: [],
  skuSale: [],
  preOrder: [],
  collectionId: null,
  loading: false,
  orderLoading: false,
  userCartLoading: false,
  addCartLoading: false,
  removeCartLoading: false,
  dashboardLoading: false,
  invoiceDataLoading:false,
  error: null,
};

export const getB2cDashboard = createAsyncThunk("b2c/dashboard", async () => {
  const response = await fetchB2cDashboard();
  return response;
});

export const fetchAllAdminUsersAsync = createAsyncThunk<B2CUser[]>(
  "b2c/fetchAllAdminUsers",
  async () => {
    const response = await fetchAllAdminUsers();
    return response;
  }
);

export const fetchAllAdminUsersOrdersAsync = createAsyncThunk<
  B2COrderDetail[],
  string
>("b2c/fetchAllAdminUsersOrders", async (UserID) => {
  const response = await fetchAllAdminUsersOrders(UserID);
  return response;
});

export const fetchAllUsersRoleAsync = createAsyncThunk<UserShipping[]>(
  "b2c/fetchAllUsersRole",
  async () => {
    const response = await fetchAllUsersRole();
    return response;
  }
);

export const updateUserRoleAsync = createAsyncThunk<AdminUserRole[], string>(
  "b2c/updateUserRole",
  async (UserID) => {
    const response = await updateUserRole(UserID);
    return response;
  }
);

export const fetchAllCouponAsync = createAsyncThunk<Coupon[]>(
  "b2c/fetchAllCoupon",
  async () => {
    const response = await fetchAllCoupon();
    return response;
  }
);

export const toggleCouponAsync = createAsyncThunk<Coupon[], number>(
  "b2c/toggleCoupon",
  async (id) => {
    const response = await toggleCoupon(id);
    return response;
  }
);

export const createCouponAsync = createAsyncThunk(
  "b2c/createCoupon",
  async (couponData: cCoupon) => {
    const response = await createCoupon(couponData);
    return response;
  }
);

export const getAllInventoryAsync = createAsyncThunk<ProductInventoryItem[]>(
  "b2c/getAllInventory",
  async () => {
    const response = await getAllInventory();
    return response;
  }
);

export const fetchAllUsersShippingAsync = createAsyncThunk<UserShipping[]>(
  "b2c/fetchAllUsersShipping",
  async () => {
    const response = await fetchAllUsersShipping();
    return response;
  }
);

export const updateUserShippingAsync = createAsyncThunk<UserShipping[], string>(
  "b2c/updateUserShipping",
  async (UserID) => {
    const response = await updateUserShipping(UserID);
    return response;
  }
);

export const fetchAllProductAndSKUAsync =
  createAsyncThunk<ProductAndSKUResponse>(
    "b2c/fetchAllProductAndSKU",
    async () => {
      const response = await fetchAllProductAndSKU();
      return response;
    }
  );
export const toggleProductStatusAsync = createAsyncThunk<
  ProductAndSKUResponse,
  number
>("b2c/toggleProductStatus", async (prod_ID) => {
  const response = await toggleProductStatus(prod_ID);
  return response;
});
export const toggleSKUStatusAsync = createAsyncThunk<
  ProductAndSKUResponse,
  number
>("b2c/toggleSKUStatus", async (SKU_ID) => {
  const response = await toggleSKUStatus(SKU_ID);
  return response;
});

export const fetchAlldealerProductSaleAsync = createAsyncThunk<ProductSale[]>(
  "b2c/fetchAlldealerProductSale",
  async () => {
    const response = await fetchAlldealerProductSale();
    return response;
  }
);

export const fetchAlldealerSKUSaleAsync = createAsyncThunk<SKUProductSale[]>(
  "b2c/fetchAlldealerSKUSale",
  async () => {
    const response = await fetchAlldealerSKUSale();
    return response;
  }
);

export const getB2cOrderDetailsAsync = createAsyncThunk(
  "b2c/getB2cOrderDetails",
  async (order_ID:string) => {
    const response = await getB2cOrderDetails(order_ID);
    return response;
  }
);

export const postUpdateB2cOrderStatus = createAsyncThunk(
  "b2c/updateB2cOrderStatus",
  async (data: UpdateOrderStatus) => {
    const response = await updateB2cOrderStatus(data);
    return response.data;
  }
);

export const updateDeliveredStatusAsync = createAsyncThunk(
  "b2c/updateDeliveredStatus",
  async (updateData: UpdateDeliveredStatus) => {
    const response = await updateDeliveredStatus(updateData);
    return response;
  }
);

export const fetchAllAdminUsersCartsAsync = createAsyncThunk<AdminAllCarts[]>(
  "b2c/fetchAllAdminUsersCarts",
  async () => {
    const response = await fetchAllAdminUsersCarts();
    return response;
  }
);

export const fetchAllUserCartsAsync = createAsyncThunk<CartResponse, number>(
  "b2c/fetchAllUserCarts",
  async (UserID) => {
    const response = await fetchAllUserCarts(UserID);
    return response;
  }
);

export const addToCartsAsync = createAsyncThunk(
  "carts/addToCarts",
  async (cartData: AddCart) => {
    const response = await addToCarts(cartData);
    return response;
  }
);
export const removeFromCartsAsync = createAsyncThunk(
  "carts/removeFromCarts",
  async (cartData: RemoveCart) => {
    const response = await removeFromCarts(cartData);
    return response;
  }
);

export const fetchAllPreOrderUserDetailsAsync = createAsyncThunk(
  "carts/fetchAllPreOrderUserDetails",
  async () => {
    const response = await fetchAllPreOrderUserDetails();

    return response;
  }
);

export const removeAllFromPreorderCartsAsync = createAsyncThunk(
  "carts/removeAllFromPreorderCarts",
  async (UserID: number) => {
    const response = await removeAllFromPreorderCarts(UserID);

    return response;
  }
);

export const getCollectionsAsync = createAsyncThunk<Collection[]>(
  "carts/getCollections",
  async () => {
    const response = await getCollections();
    return response;
  }
);

export const toggleCollectionAsync = createAsyncThunk(
  "carts/toggleCollection",
  async (collection_id: number) => {
    const response = await toggleCollection(collection_id);
    return response;
  }
);

export const getCollectionDetailAsync = createAsyncThunk<
  CollectionProductResponse,
  number
>("carts/getCollectionDetail", async (collection_id) => {
  const response = await getCollectionDetail(collection_id);
  return response;
});

export const fetchAllProductInventoryAsync = createAsyncThunk<
  ProductWithSkus[]
>("carts/fetchAllProductInventory", async () => {
  const response = await fetchAllProductInventory();
  return response;
});

export const addProductInCollectionAsync = createAsyncThunk(
  "carts/addProductInCollection",
  async (collectionData: ColData) => {
    const response = await addProductInCollection(collectionData);
    return response;
  }
);

export const removeProductFromCollectionAsync = createAsyncThunk(
  "carts/removeProductFromCollection",
  async (item_id: number) => {
    const response = await removeProductFromCollection(item_id);
    return response;
  }
);

export const editCollectionAsync = createAsyncThunk(
  "carts/editCollection",
  async (collectionData: ColName) => {
    const response = await editCollection(collectionData);
    return response;
  }
);

// Slice
export const b2cAdminSlice = createSlice({
  name: "b2cAdmin",
  initialState,
  reducers: {
    resetCollectionData: (state) => {
      state.collectionDetail = null;
    },
    resetCollectionId: (state) => {
      state.collectionId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getB2cDashboard.pending, (state) => {
        state.dashboardLoading = true;
      })
      .addCase(getB2cDashboard.fulfilled, (state, action) => {
        state.dashboard = action.payload;
        state.dashboardLoading = false;
      })
      .addCase(fetchAllAdminUsersAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllAdminUsersAsync.fulfilled, (state, action) => {
        state.users = action.payload;
        state.loading = false;
      })
      .addCase(fetchAllAdminUsersOrdersAsync.pending, (state) => {
        state.orderLoading = true;
      })
      .addCase(fetchAllAdminUsersOrdersAsync.fulfilled, (state, action) => {
        state.orders = action.payload;
        state.orderLoading = false;
      })
      .addCase(getB2cOrderDetailsAsync.pending, (state) => {
        state.invoiceDataLoading = true;
      })
      .addCase(getB2cOrderDetailsAsync.fulfilled, (state, action) => {
        state.orderB2cDetail = action.payload;
        state.invoiceDataLoading = false;
      })
      .addCase(fetchAllUsersRoleAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllUsersRoleAsync.fulfilled, (state, action) => {
        state.userRole = action.payload;
        state.loading = false;
      })
      .addCase(fetchAllCouponAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllCouponAsync.fulfilled, (state, action) => {
        state.coupon = action.payload;
        state.loading = false;
      })
      .addCase(updateUserRoleAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUserRoleAsync.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(toggleCouponAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(toggleCouponAsync.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(getAllInventoryAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllInventoryAsync.fulfilled, (state, action) => {
        state.inventory = action.payload;
        state.loading = false;
      })
      .addCase(fetchAllUsersShippingAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllUsersShippingAsync.fulfilled, (state, action) => {
        state.userShipping = action.payload;
        state.loading = false;
      })
      .addCase(updateUserShippingAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUserShippingAsync.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(fetchAllProductAndSKUAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllProductAndSKUAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.productList = action.payload;
      })
      .addCase(toggleProductStatusAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(toggleProductStatusAsync.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(toggleSKUStatusAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(toggleSKUStatusAsync.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(fetchAlldealerProductSaleAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAlldealerProductSaleAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.productSales = action.payload;
      })
      .addCase(fetchAlldealerSKUSaleAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAlldealerSKUSaleAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.skuSale = action.payload;
      })
      .addCase(updateDeliveredStatusAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateDeliveredStatusAsync.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(fetchAllAdminUsersCartsAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllAdminUsersCartsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.allCarts = action.payload;
      })
      .addCase(fetchAllUserCartsAsync.pending, (state) => {
        state.userCartLoading = true;
      })
      .addCase(fetchAllUserCartsAsync.fulfilled, (state, action) => {
        state.userCartLoading = false;
        state.userCarts = action.payload;
      })
      .addCase(getCollectionsAsync.pending, (state) => {
        state.loading = false;
      })
      .addCase(getCollectionsAsync.fulfilled, (state, action) => {
        // state.loading = false;
        state.collectionList = action.payload;
      })
      .addCase(getCollectionDetailAsync.pending, (state) => {
        state.loading = false;
      })
      .addCase(getCollectionDetailAsync.fulfilled, (state, action) => {
        // state.loading = false;
        state.collectionDetail = action.payload;
      })
      .addCase(fetchAllPreOrderUserDetailsAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllPreOrderUserDetailsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.preOrder = action.payload;
      })
      .addCase(addToCartsAsync.pending, (state) => {
        state.addCartLoading = true;
      })
      .addCase(addToCartsAsync.fulfilled, (state) => {
        state.addCartLoading = false;
      })
      .addCase(removeFromCartsAsync.pending, (state) => {
        state.removeCartLoading = true;
      })
      .addCase(removeFromCartsAsync.fulfilled, (state) => {
        state.removeCartLoading = false;
      })
      .addCase(fetchAllProductInventoryAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllProductInventoryAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.collectionInventory = action.payload;
      });
  },
});

export const { resetCollectionId, resetCollectionData } = b2cAdminSlice.actions;

export default b2cAdminSlice.reducer;
